import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface AboutPage {
  id: string;
  hero_image_url: string | null;
  title_lv: string;
  title_en: string;
  title_ru: string;
  intro_lv: string | null;
  intro_en: string | null;
  intro_ru: string | null;
  team_title_lv: string;
  team_title_en: string;
  team_title_ru: string;
  gallery_title_lv: string;
  gallery_title_en: string;
  gallery_title_ru: string;
}

export interface AboutSection {
  id: string;
  layout: string;
  title_lv: string | null;
  title_en: string | null;
  title_ru: string | null;
  body_lv: string | null;
  body_en: string | null;
  body_ru: string | null;
  image_url: string | null;
  sort_order: number;
  is_active: boolean;
}

export interface AboutTeamMember {
  id: string;
  name: string;
  role_lv: string | null;
  role_en: string | null;
  role_ru: string | null;
  photo_url: string | null;
  email?: string | null;
  phone?: string | null;
  sort_order: number;
  is_active: boolean;
}

export interface AboutGalleryImage {
  id: string;
  image_url: string;
  caption_lv: string | null;
  caption_en: string | null;
  caption_ru: string | null;
  sort_order: number;
  is_active: boolean;
}

export interface AboutStat {
  id: string;
  icon: string;
  value: string;
  label_lv: string | null;
  label_en: string | null;
  label_ru: string | null;
  sort_order: number;
  is_active: boolean;
}

export function useAboutContent() {
  const [page, setPage] = useState<AboutPage | null>(null);
  const [sections, setSections] = useState<AboutSection[]>([]);
  const [team, setTeam] = useState<AboutTeamMember[]>([]);
  const [gallery, setGallery] = useState<AboutGalleryImage[]>([]);
  const [stats, setStats] = useState<AboutStat[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;

    (async () => {
      try {
        const [p, s, t, g, st] = await Promise.all([
          supabase.from("about_page").select("*").limit(1).maybeSingle(),
          supabase.from("about_sections").select("*").eq("is_active", true).order("sort_order", { ascending: true }),
          supabase
            .from("about_team")
            .select("id,name,role_lv,role_en,role_ru,photo_url,sort_order,is_active,created_at,updated_at")
            .eq("is_active", true)
            .order("sort_order", { ascending: true }),
          supabase.from("about_gallery").select("*").eq("is_active", true).order("sort_order", { ascending: true }),
          supabase.from("about_stats").select("*").eq("is_active", true).order("sort_order", { ascending: true }),
        ]);
        if (!active) return;
        setPage((p.data as AboutPage) || null);
        setSections((s.data as AboutSection[]) || []);
        setTeam((t.data as AboutTeamMember[]) || []);
        setGallery((g.data as AboutGalleryImage[]) || []);
        setStats((st.data as AboutStat[]) || []);
      } catch (e) {
        console.error("Failed to load about content", e);
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, []);

  return { page, sections, team, gallery, stats, loading };
}
